import React, { useContext, useEffect, useState } from "react";

import {
  Checkbox,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import { Clipboard } from "react-feather";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import { Template } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_documents_pb";
import {
  DocumentQuery,
  DocumentSortType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { TemplateDescription } from "./styles";
import { listOrgTemplates } from "api/endpoints/templates-inspections";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";
import { convertToTimestamp } from "helpers/time";
import { UrlParamTypes } from "types/org-display";

interface TemplateListTableProps {
  templatesList: string[];
  setTemplatesList: (newFormStateTemplateList: string[]) => void;
}

const TemplateListTable = ({
  templatesList,
  setTemplatesList,
}: TemplateListTableProps) => {
  const classes = mainStyles();
  const { copyToClipboard } = useContext(SnackbarContext);
  const theme = useTheme();
  const [orgTemplates, setOrgTemplates] = useState<Template[]>([]);
  const { roleId, regionCode } = useParams<UrlParamTypes>();
  const { showError } = useContext(SnackbarContext);
  const [isLoading, setIsLoading] = useState(false);
  const minDate = DateTime.now().minus({ years: 2 }).startOf("day").toJSDate();

  useEffect(() => {
    const asyncFunction = async () => {
      setIsLoading(true);
      listOrgTemplates(
        {
          orgId: roleId,
          pageSize: 100,
          offset: 0,
          query: new DocumentQuery({
            ownerId: "",
            text: "",
            fromDate: convertToTimestamp(minDate),
            trashed: false,
          }),
          includeDeletedAndTrashed: false,
          sortType: DocumentSortType.NAME,
          sortDirection: SortDirection.ASC,
        },
        callOptionsHeaders(regionCode, roleId)
      )
        .then((resp) => {
          setOrgTemplates(resp.templates);
        })
        .catch(showError)
        .finally(() => setIsLoading(false));
    };
    asyncFunction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  const handleTemplateSelect = (template: Template) => {
    const selectedTemplateId = template.templateInfo?.id || "";
    let newTemplateList = [...templatesList];
    if (templatesList.includes(selectedTemplateId)) {
      newTemplateList = templatesList.filter(
        (value) => value !== selectedTemplateId
      );
    } else {
      newTemplateList.push(selectedTemplateId);
    }
    setTemplatesList(newTemplateList);
  };

  const handleTemplateSelectAll = () => {
    if (orgTemplates.length === templatesList.length) {
      setTemplatesList([]);
      return;
    }
    setTemplatesList(
      orgTemplates.map((value) => {
        return value.templateInfo?.id || "";
      })
    );
  };

  const displayTemplateRow = (template: Template) => {
    const templateID = template.templateInfo?.id || "";
    const templateName = template.templateInfo?.name || "";
    const templateDescription = template.templateInfo?.description || "";
    const templateOwnerName = template.templateInfo?.owner?.name || "";
    return (
      <TableRow data-testid={`table-row-${templateID}`} key={templateID}>
        <TableCell>
          <Checkbox
            data-testid={`template-select-${templateID}-${
              templatesList.includes(templateID) ? "selected" : "unselected"
            }`}
            color="primary"
            checked={templatesList.includes(templateID)}
            onClick={() => handleTemplateSelect(template)}
            inputProps={{
              "aria-label": "select templates",
            }}
          />
        </TableCell>
        <TableCell>
          <Typography
            color="primary"
            fontWeight="bold"
            variant="subtitle1"
            data-testid={`template-name-${templateID}`}
          >
            {templateName}
          </Typography>
          <Typography
            variant="body2"
            align="left"
            sx={classes.clickable}
            onClick={() => copyToClipboard(templateID)}
            noWrap={true}
            data-testid={`template-id-${templateID}`}
          >
            {templateID}
            <Clipboard
              color={theme.palette.text.primary}
              size={"1rem"}
              className="position-icon"
            />
          </Typography>
        </TableCell>
        <TableCell>
          <TemplateDescription
            data-testid={`template-description-${templateID}`}
          >
            {templateDescription}
          </TemplateDescription>
        </TableCell>
        <TableCell data-testid={`template-owner-name-${templateID}`}>
          {templateOwnerName}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Grid
      spacing={4}
      container
      direction="row"
      alignItems="center"
      paddingTop={1}
      data-testid="template-list-table"
    >
      <Grid item xs={12}>
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer
            sx={{
              maxHeight: 440,
              border: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Checkbox
                      color="primary"
                      data-testid={`template-select-all-${
                        orgTemplates.length === templatesList.length
                          ? "selected"
                          : "unselected"
                      }`}
                      checked={orgTemplates.length === templatesList.length}
                      onClick={handleTemplateSelectAll}
                    />
                    <b>Select All</b>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Sort by name">
                      <b>Template Name</b>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <b>Description</b>
                  </TableCell>
                  <TableCell>
                    <b>Owner</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  orgTemplates.map((template) => displayTemplateRow(template))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TemplateListTable;
